/*
 * ce-countdown.js — per-visitor countdown for the announcement bar (snippets/ce-countdown.liquid).
 *
 * Why a cookie and not a fixed date: the offer is "X hours left for YOU". The first visit sets a deadline
 * (now + duration) in a cookie named after the block's `timer_key`; every later page view — same tab, new tab,
 * a week later — reads that deadline back, so the visitor sees the time that is really left, never a reset.
 * The merchant restarts everyone by changing the key (new cookie name). If the merchant shortens the duration,
 * deadlines beyond the new duration are dropped and start again, so nobody sees more time than the setting.
 *
 * At zero: with `data-ce-cd-restart` a fresh cycle starts (evergreen); without it the boxes stay at 00.
 * One element per page is expected; several work independently as long as their keys differ.
 */
(() => {
  if (customElements.get('ce-countdown')) return;

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=(\\d+)'));
    return match ? Number(match[1]) : NaN;
  };
  const writeCookie = (name, value) => {
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`;
  };
  const two = (n) => String(n).padStart(2, '0');

  class CeCountdown extends HTMLElement {
    connectedCallback() {
      this.cookieName = 'ce_cd_' + (this.dataset.ceCdKey || 'sale').replace(/[^a-z0-9_-]/gi, '');
      this.duration = Math.max(0, Number(this.dataset.ceCdSeconds) || 0) * 1000;
      this.restart = this.hasAttribute('data-ce-cd-restart');
      this.fields = {
        h: this.querySelector('[data-ce-cd="h"]'),
        m: this.querySelector('[data-ce-cd="m"]'),
        s: this.querySelector('[data-ce-cd="s"]'),
      };
      this.deadline = this.loadDeadline();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    loadDeadline() {
      const now = Date.now();
      let deadline = readCookie(this.cookieName);
      if (!Number.isFinite(deadline) || deadline > now + this.duration) {
        deadline = now + this.duration;
        writeCookie(this.cookieName, deadline);
      }
      return deadline;
    }

    tick() {
      let left = this.deadline - Date.now();
      if (left <= 0) {
        if (this.restart && this.duration > 0) {
          this.deadline = Date.now() + this.duration;
          writeCookie(this.cookieName, this.deadline);
          left = this.duration;
        } else {
          left = 0;
        }
      }
      const total = Math.floor(left / 1000);
      const values = { h: Math.floor(total / 3600), m: Math.floor((total % 3600) / 60), s: total % 60 };
      for (const key of Object.keys(values)) {
        const el = this.fields[key];
        if (!el) continue;
        const text = two(values[key]);
        if (el.textContent !== text) el.textContent = text;
      }
    }
  }

  customElements.define('ce-countdown', CeCountdown);
})();
